import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import {
  parseDfAiSummaryBlocks,
  stripDfSummaryReportFooter,
  stripInlineBoldMarkers,
} from "../../../utils/dfAiSummaryParse";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 14,
    color: "#1f2937",
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
  },
  headingRow: {
    marginBottom: 6,
  },
  docTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#0f172a",
  },
  generatedAt: {
    fontSize: 13.5,
    color: "#64748b",
    marginBottom: 10,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    marginBottom: 10,
  },
  sectionHeading: {
    fontSize: 18.5,
    fontWeight: 700,
    marginTop: 10,
    marginBottom: 5,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    color: "#0f172a",
  },
  subheading: {
    fontSize: 14.5,
    fontWeight: 700,
    marginTop: 6,
    marginBottom: 3,
    color: "#1e293b",
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 1.55,
    marginBottom: 4,
    color: "#334155",
  },
  bulletRow: {
    flexDirection: "row",
    marginBottom: 3,
    paddingLeft: 8,
  },
  bulletGlyph: {
    width: 10,
    fontSize: 14,
    color: "#334155",
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 1.5,
    color: "#334155",
  },
  reportFooter: {
    marginTop: 14,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    fontSize: 8,
    color: "#64748b",
  },
  tableBrief: {
    fontSize: 15.5,
    fontWeight: 700,
    color: "#475569",
    marginTop: 4,
    marginBottom: 3,
    lineHeight: 1.4,
  },
  table: {
    width: "100%",
    marginTop: 2,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#cbd5e1",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    minHeight: 18,
  },
  tableRowHeader: {
    flexDirection: "row",
    backgroundColor: "#f1f5f9",
    borderBottomWidth: 1,
    borderBottomColor: "#cbd5e1",
    minHeight: 20,
  },
  tableCell: {
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 5,
    fontSize: 12.5,
    borderRightWidth: 1,
    borderRightColor: "#e2e8f0",
    justifyContent: "center",
  },
  tableCellLast: {
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 5,
    fontSize: 12.5,
    justifyContent: "center",
  },
  tableHeaderText: {
    fontSize: 12.5,
    fontWeight: 700,
    textAlign: "center",
    color: "#0f172a",
  },
  tableBodyText: {
    fontSize: 14.5,
    textAlign: "left",
    color: "#334155",
  },
  tableBodyTextCenter: {
    fontSize: 15.5,
    textAlign: "center",
    color: "#334155",
  },
});

const PdfDocumentDfAiSummary = ({ source, summary }) => {
  const cleaned = stripDfSummaryReportFooter(summary || "No AI summary available.");

  const blocks = parseDfAiSummaryBlocks(cleaned);

  const formatPriceCell = (value) => {
    const raw = stripInlineBoldMarkers(String(value ?? "")).trim();
    return raw.replace(/NZ\$/gi, "$");
  };

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.headingRow}>
          <Text style={styles.docTitle}>AI Summary</Text>
        </View>
        <Text style={styles.generatedAt}>
          Source: {source || "—"} · Report generated as of {new Date().toLocaleDateString()}
        </Text>
        <View style={styles.divider} />

        {blocks.map((block, idx) => {
          if (block.type === "heading") {
            return (
              <Text key={`h-${idx}`} style={styles.sectionHeading} wrap>
                {stripInlineBoldMarkers(block.text)}
              </Text>
            );
          }
          if (block.type === "subheading") {
            return (
              <Text key={`sh-${idx}`} style={styles.subheading} wrap>
                {stripInlineBoldMarkers(block.text)}
              </Text>
            );
          }
          if (block.type === "bullets") {
            return (
              <View key={`bl-${idx}`}>
                {block.items.map((item, j) => (
                  <View key={`b-${idx}-${j}`} style={styles.bulletRow} wrap={false}>
                    <Text style={styles.bulletGlyph}>•</Text>
                    <Text style={styles.bulletText} wrap>
                      {stripInlineBoldMarkers(item)}
                    </Text>
                  </View>
                ))}
              </View>
            );
          }
          if (block.type === "footer") {
            return null;
          }
          if (block.type === "table") {
            let header = block.rows[0] || [];
            const wowIdx = header.findIndex((h) => /wow/i.test(String(h)));
            if (wowIdx !== -1) {
              header = header.filter((_, i2) => i2 !== wowIdx);
            }
            const body = block.rows
              .slice(1)
              .map((r) => (wowIdx !== -1 ? r.filter((_, i2) => i2 !== wowIdx) : r));
            return (
              <View key={`t-${idx}`} wrap={false}>
                {block.brief ? (
                  <Text style={styles.tableBrief} wrap>
                    {stripInlineBoldMarkers(block.brief)}
                  </Text>
                ) : null}
                <View style={styles.table} wrap={false}>
                <View style={styles.tableRowHeader}>
                  {header.map((h, hIdx) => (
                    <View
                      key={`th-${hIdx}`}
                      style={hIdx === header.length - 1 ? styles.tableCellLast : styles.tableCell}
                    >
                      <Text style={styles.tableHeaderText}>{stripInlineBoldMarkers(String(h))}</Text>
                    </View>
                  ))}
                </View>
                {body.map((row, rIdx) => (
                  <View key={`tr-${rIdx}`} style={styles.tableRow} wrap={false}>
                    {row.map((cell, cIdx) => (
                      <View
                        key={`td-${cIdx}`}
                        style={cIdx === row.length - 1 ? styles.tableCellLast : styles.tableCell}
                      >
                        <Text style={cIdx > 0 ? styles.tableBodyTextCenter : styles.tableBodyText} wrap>
                          {formatPriceCell(cell)}
                        </Text>
                      </View>
                    ))}
                  </View>
                ))}
                </View>
              </View>
            );
          }
          return (
            <Text key={`p-${idx}`} style={styles.paragraph} wrap>
              {stripInlineBoldMarkers(block.text || " ")}
            </Text>
          );
        })}
      </Page>
    </Document>
  );
};

export default PdfDocumentDfAiSummary;
